import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { TiltCard } from "./TiltCard";

const PROGRAMS = [
  {
    id: "toddler",
    title: "Toddler",
    age: "1.5 – 2.5 years",
    desc: "Gentle sensory play, first words and wobbly first friendships — at exactly their pace.",
    img: "https://images.unsplash.com/photo-1786292949404-084cbd10c7b1?crop=entropy&cs=srgb&fm=jpg&q=85",
    tint: "bg-apricot",
  },
  {
    id: "playgroup",
    title: "Playgroup",
    age: "2.5 – 3.5 years",
    desc: "Rhymes, stories and pretend play that quietly build language and confidence.",
    img: "https://images.unsplash.com/photo-1761208663763-c4d30657c910?crop=entropy&cs=srgb&fm=jpg&q=85",
    tint: "bg-mint",
  },
  {
    id: "nursery",
    title: "Nursery & Junior KG",
    age: "3.5 – 5 years",
    desc: "Phonics, numbers, patterns and art — early literacy through hands-on discovery.",
    img: "https://images.unsplash.com/photo-1777056481869-feac70afe522?crop=entropy&cs=srgb&fm=jpg&q=85",
    tint: "bg-[#FFF6D6]",
  },
  {
    id: "seniorkg",
    title: "Senior KG",
    age: "5 – 6 years",
    desc: "School readiness with independent problem-solving, expressive language and big-kid pride.",
    img: "https://images.unsplash.com/photo-1777056491418-d4ff81a4ad92?crop=entropy&cs=srgb&fm=jpg&q=85",
    tint: "bg-[#E0F2FE]",
  },
];

export const Programs = ({ onEnquire }) => (
  <section id="programs" data-testid="programs-section" className="relative py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="max-w-2xl">
        <span className="inline-block text-xs font-bold tracking-[0.24em] uppercase text-peach mb-4">
          Our Programmes
        </span>
        <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-ink">
          The right start for{" "}
          <em className="font-quote italic font-medium text-sage">every age</em>
        </h2>
        <p className="mt-4 text-ink/60 text-base sm:text-lg">
          Four gentle stages, one continuous journey — designed around how young children
          actually learn.
        </p>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
        {PROGRAMS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <TiltCard max={5} className="h-full">
              <motion.article
                data-testid={`program-card-${p.id}`}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className={`h-full rounded-[2rem] p-4 sm:p-5 border border-ink/5 ${p.tint} soft-shadow hover:soft-shadow-lg`}
              >
                <div className="rounded-[1.4rem] overflow-hidden">
                  <img
                    src={p.img}
                    alt={`${p.title} at Kiwi Kids`}
                    loading="lazy"
                    className="w-full h-52 sm:h-60 object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
                <div className="p-3 sm:p-4 flex items-start justify-between gap-4">
                  <div>
                    <span className="inline-block font-play text-[11px] font-semibold uppercase tracking-wider text-sage bg-white/80 rounded-full px-3 py-1 mb-2.5">
                      {p.age}
                    </span>
                    <h3 className="font-display font-bold text-xl sm:text-2xl text-ink">{p.title}</h3>
                    <p className="mt-2 text-sm sm:text-[15px] text-ink/60 leading-relaxed">{p.desc}</p>
                  </div>
                  <motion.button
                    onClick={() => onEnquire("admissions")}
                    data-testid={`program-enquire-${p.id}`}
                    aria-label={`Enquire about ${p.title}`}
                    whileHover={{ rotate: 45, scale: 1.08 }}
                    whileTap={{ scale: 0.9 }}
                    className="shrink-0 w-11 h-11 rounded-full bg-ink text-cream flex items-center justify-center"
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </motion.button>
                </div>
              </motion.article>
            </TiltCard>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
